import React, { useState } from 'react';
import { Mail, Download, Trash2, AlertTriangle, Loader2 } from 'lucide-react';
import { api } from '../utils/api';
import { useAuth } from '../context/AuthContext';
import { useLocalizedNavigate } from '../i18n/useLocalizedNav';

const SECTIONS = [
  { id: 'summary', label: 'Plain-language summary' },
  { id: 'collect', label: 'What we collect' },
  { id: 'use', label: 'How we use your data' },
  { id: 'retention', label: 'Retention & deletion' },
  { id: 'rights', label: 'Your rights' },
  { id: 'cookies', label: 'Cookies & analytics' },
  { id: 'transfers', label: 'International transfers' },
  { id: 'updates', label: 'Updates to this policy' },
];

const COLLECT = [
  ['Account basics', 'Full name, email, language preference, password hash.'],
  ['Stamp history', 'Venue ID, timestamp, signed claim code, multiplier applied.'],
  ['Device metadata', 'Browser user-agent, OS family, approximate region (city-level, IP-derived).'],
  ['Payments', 'None — Aranor is free. Vendor discounts settle at venue.'],
  ['Support tickets', 'Anything you write to us at contact@ — kept for 18 months.'],
  ['Photos', 'Only if you upload an avatar. Stored encrypted at rest.'],
];

const RETENTION = [
  ['Active passport', 'Kept while your account is active.'],
  ['Inactive account', 'Auto-archived after 24 months of no logins; deleted 6 months later.'],
  ['Stamp history', 'Anonymized after account deletion (we keep counts for vendor settlements).'],
  ['Support tickets', '18 months from last reply.'],
  ['Analytics', 'No cookies; aggregated daily, raw events purged after 30 days.'],
];

const Privacy: React.FC = () => {
  const navigate = useLocalizedNavigate();
  const { user, logout } = useAuth();
  const [showDelete, setShowDelete] = useState(false);
  const [confirmText, setConfirmText] = useState('');
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState('');
  const [downloading, setDownloading] = useState(false);

  const handleDownload = async () => {
    if (!user) {
      navigate('/login');
      return;
    }
    setDownloading(true);
    try {
      const data = await api.get('/auth/account/export');
      if (data?.error) {
        setDownloading(false);
        return;
      }
      const blob = new Blob([JSON.stringify(data, null, 2)], {
        type: 'application/json',
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `aranor-passport-${user.email}-${
        new Date().toISOString().split('T')[0]
      }.json`;
      a.click();
      URL.revokeObjectURL(url);
    } finally {
      setDownloading(false);
    }
  };

  const handleDelete = async () => {
    setDeleting(true);
    setDeleteError('');
    try {
      const res = await api.delete('/auth/account');
      if (res?.error) {
        setDeleteError(res.error);
        setDeleting(false);
        return;
      }
      logout();
      navigate('/');
    } catch {
      setDeleteError('Could not delete the account. Please try again.');
      setDeleting(false);
    }
  };

  return (
  <div className="space-y-12">
    {/* Hero */}
    <section className="-mx-4 px-6 lg:px-10 py-12 bg-brand-secondary text-white rounded-3xl space-y-5">
      <p className="font-heading font-bold text-xs tracking-[0.25em] text-brand-accent uppercase">
        Document · 01
      </p>
      <h1 className="font-display text-4xl md:text-6xl leading-[1.05] text-warm-100/80">
        Your data,{' '}
        <span className="italic text-brand-primary">stamped &amp; sealed</span>.
      </h1>
      <p className="text-warm-500 max-w-2xl leading-relaxed">
        Aranor is a travel passport — collecting stamps, not surveillance. This
        page is the plain-English receipt of every byte of personal data we
        touch, why we touch it, and how to ask for it back.
      </p>
      <div className="flex flex-wrap gap-3 text-[10px] font-heading font-bold tracking-[0.15em] uppercase">
        <span className="px-3 py-1.5 bg-white/5 rounded-lg text-warm-500">
          Last updated · May 19 · 2026
        </span>
        <span className="px-3 py-1.5 bg-white/5 rounded-lg text-warm-500">
          Effective · Jan 01 · 2026
        </span>
        <span className="px-3 py-1.5 bg-brand-primary text-brand-secondary rounded-lg">
          Privacy Policy
        </span>
      </div>
    </section>

    <div className="grid grid-cols-1 lg:grid-cols-[240px_1fr] gap-10">
      {/* Sidebar */}
      <aside className="hidden lg:block space-y-6 h-fit sticky top-24">
        <div className="space-y-2">
          <p className="font-heading font-bold text-[11px] tracking-[0.2em] text-warm-500 uppercase">
            On this page
          </p>
          <ul className="space-y-1">
            {SECTIONS.map((s, i) => (
              <li key={s.id}>
                <a
                  href={`#${s.id}`}
                  className="block text-sm text-warm-600 hover:text-brand-accent transition-colors py-1"
                >
                  <span className="text-warm-400 mr-2">
                    {String(i + 1).padStart(2, '0')}
                  </span>
                  {s.label}
                </a>
              </li>
            ))}
          </ul>
        </div>
        <div className="bg-brand-secondary text-white rounded-2xl p-5 space-y-1">
          <p className="font-heading font-bold text-[10px] tracking-[0.2em] text-brand-accent uppercase">
            Need a human?
          </p>
          <a
            href="mailto:contact@aranortravel.com"
            className="flex items-start gap-2 font-heading font-bold text-sm text-brand-primary break-all"
          >
            <Mail className="w-4 h-4 mt-0.5 shrink-0" />
            <span className="break-all">contact@aranortravel.com</span>
          </a>
          <p className="text-warm-500 text-xs">
            We reply within 3 business day, in EN / HY / RU.
          </p>
        </div>
      </aside>

      {/* Body */}
      <div className="space-y-14 max-w-3xl">
        <section id="summary" className="scroll-mt-24 space-y-4">
          <p className="font-heading font-bold text-xs tracking-[0.2em] text-brand-accent uppercase">
            § 01 · TL;DR
          </p>
          <h2 className="font-display text-3xl text-warm-800">
            Plain-language summary
          </h2>
          <div className="bg-brand-success/10 border border-brand-success/20 rounded-2xl p-5 space-y-2 text-warm-700 text-sm leading-relaxed">
            <p className="font-bold text-warm-800">The five-line version</p>
            <ul className="space-y-1 list-disc list-inside">
              <li>We collect your email, name, language, and the venues you stamp.</li>
              <li>We use that to grow your rank, hand out discounts, and detect fraud.</li>
              <li>We never sell your data. Period.</li>
              <li>You can export everything or delete your passport from Profile → Data.</li>
              <li>We use one analytics tool — Plausible — which doesn't track you across sites.</li>
            </ul>
          </div>
          <p className="text-warm-600 leading-relaxed">
            Aranor LLC ("we", "us") operates the Aranor Passport service
            available at passport.aranortravel.com and the connected mobile
            applications. This Privacy Policy explains how we handle Personal
            Data when you create a passport, stamp a venue, or simply browse the
            site.
          </p>
        </section>

        <section id="collect" className="scroll-mt-24 space-y-4">
          <p className="font-heading font-bold text-xs tracking-[0.2em] text-brand-accent uppercase">
            § 02 · Data inventory
          </p>
          <h2 className="font-display text-3xl text-warm-800">
            What we collect
          </h2>
          <p className="text-warm-500">
            We deliberately keep this list short. If a field isn't on it, we
            don't collect it.
          </p>
          <dl className="divide-y divide-warm-200 border border-warm-200 rounded-2xl overflow-hidden">
            {COLLECT.map(([k, v]) => (
              <div key={k} className="grid grid-cols-1 sm:grid-cols-[180px_1fr] gap-1 sm:gap-4 p-4">
                <dt className="font-heading font-bold text-[11px] tracking-[0.12em] text-warm-500 uppercase">
                  {k}
                </dt>
                <dd className="text-warm-700 text-sm">{v}</dd>
              </div>
            ))}
          </dl>
        </section>

        <section id="use" className="scroll-mt-24 space-y-4">
          <p className="font-heading font-bold text-xs tracking-[0.2em] text-brand-accent uppercase">
            § 03 · Purposes
          </p>
          <h2 className="font-display text-3xl text-warm-800">
            How we use your data
          </h2>
          <ul className="space-y-3 text-warm-700">
            <li>
              <span className="font-bold text-warm-800">Run your passport</span>{' '}
              — record stamps, compute rank, surface eligible discounts.
            </li>
            <li>
              <span className="font-bold text-warm-800">Verify legitimacy</span>{' '}
              — the staff-code handshake exists to keep fraudulent stamps out.
            </li>
            <li>
              <span className="font-bold text-warm-800">Improve the product</span>{' '}
              — aggregated, never linked to your name in analytics.
            </li>
            <li>
              <span className="font-bold text-warm-800">Talk to you</span> —
              receipts, rank-up notifications, and the occasional venue invite.
            </li>
          </ul>
          <div className="bg-brand-danger/10 border border-brand-danger/20 rounded-2xl p-5 space-y-1">
            <p className="font-bold text-warm-800 text-sm">
              What we will never do
            </p>
            <p className="text-warm-600 text-sm leading-relaxed">
              Sell your data, target advertising at you, share your stamp
              history with other users without your action, or use your face for
              any biometric purpose.
            </p>
          </div>
        </section>

        <section id="retention" className="scroll-mt-24 space-y-4">
          <p className="font-heading font-bold text-xs tracking-[0.2em] text-brand-accent uppercase">
            § 04 · Lifecycle
          </p>
          <h2 className="font-display text-3xl text-warm-800">
            Retention &amp; deletion
          </h2>
          <p className="text-warm-500">
            Data lives only as long as it's useful. Defaults below; you can
            override most of them.
          </p>
          <dl className="divide-y divide-warm-200 border border-warm-200 rounded-2xl overflow-hidden">
            {RETENTION.map(([k, v]) => (
              <div key={k} className="grid grid-cols-1 sm:grid-cols-[180px_1fr] gap-1 sm:gap-4 p-4">
                <dt className="font-heading font-bold text-[11px] tracking-[0.12em] text-warm-500 uppercase">
                  {k}
                </dt>
                <dd className="text-warm-700 text-sm">{v}</dd>
              </div>
            ))}
          </dl>
        </section>

        <section id="rights" className="scroll-mt-24 space-y-4">
          <p className="font-heading font-bold text-xs tracking-[0.2em] text-brand-accent uppercase">
            § 05 · GDPR
          </p>
          <h2 className="font-display text-3xl text-warm-800">Your rights</h2>
          <p className="text-warm-500">
            Whether you live in the EU or not, you can:
          </p>
          <ul className="space-y-3 text-warm-700">
            <li>
              <span className="font-bold text-warm-800">Access</span> — download
              a JSON dump of everything we hold.
            </li>
            <li>
              <span className="font-bold text-warm-800">Rectify</span> — fix
              anything wrong in Profile → Account.
            </li>
            <li>
              <span className="font-bold text-warm-800">Erase</span> — delete
              your passport in one click; we comply within 30 days.
            </li>
            <li>
              <span className="font-bold text-warm-800">Object</span> — withdraw
              consent for non-essential processing at any time.
            </li>
            <li>
              <span className="font-bold text-warm-800">Lodge a complaint</span>{' '}
              — with the Personal Data Protection Agency of Armenia.
            </li>
          </ul>
        </section>

        <section id="cookies" className="scroll-mt-24 space-y-4">
          <p className="font-heading font-bold text-xs tracking-[0.2em] text-brand-accent uppercase">
            § 06 · Storage
          </p>
          <h2 className="font-display text-3xl text-warm-800">
            Cookies &amp; analytics
          </h2>
          <p className="text-warm-600 leading-relaxed">
            We use one first-party session cookie (<code>ar_sid</code>) to keep
            you signed in. That's it. No third-party tracking, no
            fingerprinting. Analytics are handled by Plausible without any
            cookie at all.
          </p>
        </section>

        <section id="transfers" className="scroll-mt-24 space-y-4">
          <p className="font-heading font-bold text-xs tracking-[0.2em] text-brand-accent uppercase">
            § 07 · Jurisdictions
          </p>
          <h2 className="font-display text-3xl text-warm-800">
            International transfers
          </h2>
          <p className="text-warm-600 leading-relaxed">
            Aranor is based in Yerevan, Armenia. Most of our infrastructure sits
            in the EU. Where data must move between jurisdictions, we rely on
            the European Commission's adequacy decisions and standard
            contractual clauses.
          </p>
        </section>

        <section id="updates" className="scroll-mt-24 space-y-4">
          <p className="font-heading font-bold text-xs tracking-[0.2em] text-brand-accent uppercase">
            § 08 · Revisions
          </p>
          <h2 className="font-display text-3xl text-warm-800">
            Updates to this policy
          </h2>
          <p className="text-warm-600 leading-relaxed">
            Material changes are announced via email at least 30 days before
            they take effect. Cosmetic changes (typos, formatting) are made
            silently and reflected in the "Last updated" date at the top.
          </p>
        </section>

        <div className="flex flex-col sm:flex-row sm:items-center gap-4 pt-6 border-t border-warm-200">
          <button
            onClick={handleDownload}
            disabled={downloading}
            className="inline-flex items-center justify-center gap-2 px-6 py-3.5 bg-brand-primary text-brand-secondary font-heading font-bold rounded-xl hover:bg-brand-accent transition-all disabled:opacity-60"
          >
            {downloading ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Download className="w-4 h-4" />
            )}
            Download my data
          </button>
          <button
            onClick={() =>
              user ? setShowDelete(true) : navigate('/login')
            }
            className="inline-flex items-center justify-center gap-2 px-6 py-3.5 bg-white border border-brand-danger/40 text-brand-danger font-heading font-bold rounded-xl hover:bg-brand-danger/10 transition-all"
          >
            <Trash2 className="w-4 h-4" />
            Delete my passport
          </button>
          <span className="text-warm-500 text-sm sm:ml-auto">
            v · 2026.05.19
          </span>
        </div>
      </div>
    </div>

    {/* Permanent deletion — requires typing "confirm". */}
    {showDelete && (
      <div className="fixed inset-0 z-50 bg-warm-800/80 backdrop-blur-sm flex items-center justify-center p-4">
        <div className="bg-white rounded-3xl w-full max-w-md p-8 space-y-5">
          <div className="w-14 h-14 bg-brand-danger/15 rounded-full flex items-center justify-center">
            <AlertTriangle className="w-7 h-7 text-brand-danger" />
          </div>
          <div className="space-y-2">
            <h2 className="font-display text-2xl text-warm-800">
              Delete your passport?
            </h2>
            <p className="text-warm-600 text-sm leading-relaxed">
              This permanently erases your account, every stamp, and all
              associated data. It cannot be undone.
            </p>
          </div>
          <div className="space-y-2">
            <label className="block text-sm text-warm-600">
              Type <span className="font-bold text-warm-800">confirm</span> to
              proceed.
            </label>
            <input
              type="text"
              autoFocus
              autoComplete="off"
              placeholder="confirm"
              value={confirmText}
              onChange={(e) => setConfirmText(e.target.value)}
              className="w-full px-4 py-3 bg-warm-100 rounded-xl outline-none focus:ring-2 focus:ring-brand-danger font-mono"
            />
          </div>
          {deleteError && (
            <p className="text-brand-danger text-sm font-bold">
              {deleteError}
            </p>
          )}
          <div className="flex gap-3 pt-1">
            <button
              onClick={() => {
                setShowDelete(false);
                setConfirmText('');
                setDeleteError('');
              }}
              disabled={deleting}
              className="flex-1 py-3 font-heading font-bold text-warm-600 hover:bg-warm-100 rounded-xl transition-colors disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              onClick={handleDelete}
              disabled={confirmText !== 'confirm' || deleting}
              className="flex-1 py-3 bg-brand-danger text-white font-heading font-bold rounded-xl hover:opacity-90 transition-all disabled:opacity-40 disabled:cursor-not-allowed inline-flex items-center justify-center gap-2"
            >
              {deleting ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                'Delete forever'
              )}
            </button>
          </div>
        </div>
      </div>
    )}
  </div>
  );
};

export default Privacy;
